import { useOrganizationStore } from '../stores/useOrganizationStore';
import { createCloudRunAxios } from './axios';

const remiApi = createCloudRunAxios('remi-api');

/**
 * Calculate lead score for a single contact
 * @param contactId The contact ID to score
 * @param organizationId The organization ID
 * @param forceRefresh Whether to force a new calculation (bypasses 24h cache)
 * @returns The response from the API with the lead score details
 */
export const scoreContact = async (
  contactId: string,
  organizationId: string,
  forceRefresh: boolean = false
) => {
  try {
    const response = await remiApi.post('/api/v1/scoring/score', {
      contact_id: contactId,
      organization_id: organizationId,
      force_refresh: forceRefresh,
    });

    // Cap the score at 99 if it's 100 or higher
    if (response.data && response.data.data && response.data.data.score >= 100) {
      response.data.data.score = 99;
    }

    return response.data;
  } catch (error) {
    console.error('Error scoring contact:', error);
    throw error;
  }
};

/**
 * Calculate scores for multiple contacts in one request
 * @param contactIds Array of contact IDs to score (max 50)
 * @param organizationId The organization ID
 * @param forceRefresh Whether to force new calculations
 * @returns The response from the API with the batch scoring results
 */
export const batchScoreContacts = async (
  contactIds: string[],
  organizationId: string,
  forceRefresh: boolean = false
) => {
  try {
    const response = await remiApi.post('/api/v1/scoring/score-batch', {
      contact_ids: contactIds,
      organization_id: organizationId,
      force_refresh: forceRefresh,
    });

    return response.data;
  } catch (error) {
    console.error('Error batch scoring contacts:', error);
    throw error;
  }
};

/**
 * Retrieve current score and historical scoring records for a contact
 * @param contactId The contact ID
 * @param organizationId The organization ID
 * @param includeHistory Whether to include scoring history
 * @returns The response from the API with the contact's score and history
 */
export const getContactScore = async (
  contactId: string,
  organizationId: string,
  includeHistory: boolean = false
) => {
  try {
    const response = await remiApi.get(`/api/v1/scoring/${contactId}`, {
      params: { organization_id: organizationId, include_history: includeHistory },
    });

    return response.data;
  } catch (error) {
    console.error('Error getting contact score:', error);
    throw error;
  }
};

/**
 * Hook to get the current organization ID and score a contact
 * @returns Object with scoreSelectedContact function
 */
export const useLeadScoring = () => {
  const { organization } = useOrganizationStore();

  /**
   * Score a contact using the current organization ID
   * @param contactId The contact ID to score
   * @param forceRefresh Whether to force a new calculation
   * @returns The response from the API with the lead score details
   */
  const scoreSelectedContact = async (contactId: string, forceRefresh: boolean = false) => {
    if (!organization?.id) {
      throw new Error('No organization selected');
    }

    return scoreContact(contactId, organization.id, forceRefresh);
  };

  return {
    scoreSelectedContact,
  };
};
